import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthService } from '@core/auth/auth.service';
import { Feature, FeatureType } from '@core/models/features/feature.model';
import { Organisation, needsOrganisationOnboarding } from '@core/models/organisation/organisation.model';
import { BranchService } from '@core/services/branch.service';
import { FeatureService } from '@core/services/feature.service';
import { OrganisationService } from '@core/services/organisation.service';
import { WorkSpaceService } from '@core/services/workspace.service';
import { Observable, filter, map, of, take } from 'rxjs';


@Injectable({
  providedIn: 'root',
})
export class LeaveWorkSpaceGuard{
  constructor(private workspaceService: WorkSpaceService, private organisationService: OrganisationService,
) {}

  canDeactivate(component: any, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState?: RouterStateSnapshot): boolean {
    const navigatingToOws = nextState?.url.startsWith('/ows/');
    if(!navigatingToOws){
      this.organisationService.deactivateOrg();
      this.workspaceService.setUserWorkSpaceActive(true);
    }
    return true;
  }
}


@Injectable({
  providedIn: 'root',
})
export class WorkSpaceGuard{
  // Sections of /ows/:id that remain reachable while a branch vanity domain is active. Everything
  // else (dashboard, learning-plans, analytics, certificates, forms, ...) redirects to members.
  private static readonly VANITY_ALLOWED_SECTIONS = ['members', 'enrolments', 'connections', 'settings', 'verify'];

  constructor(
    private router: Router,
    private featureService:FeatureService,
    private branchService: BranchService,
    private authService: AuthService) {}


  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    if (this.authService.IsLoggedIn) {
      const workspaceId = route.root.firstChild?.paramMap.get('id');

      if(this.branchService.vanityMode){
        const section = state.url.split('?')[0].split('/')[3] ?? '';
        if(!WorkSpaceGuard.VANITY_ALLOWED_SECTIONS.includes(section)){
          return of(this.router.createUrlTree(['/ows', workspaceId, 'members']));
        }
      }

      // A route names the feature it needs, or several of which any one is enough.
      const requiredFeature = route.data['feature'];
      const requiredFeatures: FeatureType[] = Array.isArray(requiredFeature) ? requiredFeature : [requiredFeature];
      if(requiredFeature != undefined){

        return this.featureService.features$.pipe(filter((ws): ws is Feature[] => ws !== null),take(1),map((f) =>{
          if(requiredFeatures.some(feature => this.featureService.hasFeature(feature))){
            return true;
          }else{

            // In vanity mode never fall back to dashboard/certificates (both are blocked above);
            // keep the user on members, or drop to the personal dashboard if they lack access.
            if (!this.branchService.vanityMode && this.featureService.hasFeature(FeatureType.DASHBOARD_ACCESS)) {
               return this.router.createUrlTree(['/ows', workspaceId, 'dashboard']);
            }else if (!this.branchService.vanityMode && this.featureService.hasFeature(FeatureType.CERTIFICATES_ACCESS)) {
              return this.router.createUrlTree(['/ows', workspaceId, 'certificates']);
            } else if (this.featureService.hasFeature(FeatureType.MEMBERS_ACCESS)) {
              return this.router.createUrlTree(['/ows', workspaceId, 'members']);
            }else{
              return this.router.createUrlTree(['/dashboard']);
            }

          };
        }));

      }else{
        return of(true);
      }
    }else{
      return of(this.router.createUrlTree(['/login']));
    }

  }
}

@Injectable({
  providedIn: 'root',
})
export class VerificationGuard {
  constructor(
    private router: Router,
    private organisationService: OrganisationService,
    private branchService: BranchService,
    private authService: AuthService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    if (!this.authService.IsLoggedIn) {
      return of(this.router.createUrlTree(['/login']));
    }
    const workspaceId = route.root.firstChild?.paramMap.get('id');
    return this.organisationService.currentOrganisation$.pipe(
      // The workspace that is being opened, not whichever one was open before it.
      filter((o): o is Organisation => o !== null && (!workspaceId || o.id === workspaceId)),
      take(1),
      map(org => {
        // A masterpass token: someone from Smart Lions is looking at a workspace that is not theirs,
        // so neither the member verification nor the onboarding is theirs to complete.
        const impersonating = !!this.authService.getToken().isImpersonating;
        if (org.isPremium && !org.verified && !impersonating) {
          return this.router.createUrlTree(['/ows', workspaceId, 'verify']);
        }
        // The onboarding flow still owes this workspace something: a department or a joint committee
        // is missing, or the company was never subscribed at all. Only an admin can settle either,
        // so only an admin is sent there — and never on a vanity domain, which is restricted to
        // pages that need none of it.
        if (org.admin && !impersonating && !this.branchService.vanityMode && needsOrganisationOnboarding(org)) {
          return this.router.createUrlTree(['/ows', workspaceId, 'onboarding'], { queryParams: { returnUrl: state.url } });
        }
        return true;
      })
    );
  }
}

@Injectable({
  providedIn: 'root',
})
export class WorkSpaceLoaderGuard{
  constructor(
    private router: Router,
    private organisationService:OrganisationService,
    private workspaceService:WorkSpaceService,
    private authService: AuthService) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean|UrlTree> {
    const id = route.params['id'];
    if (this.authService.IsLoggedIn) {
      if(!id){
        return of(this.router.createUrlTree(['/dashboard']));
      }
      const cachedWorkspaces = this.workspaceService.orgWorkSpaces$.getValue();
      const cachedWs = cachedWorkspaces?.find(ws => ws.id === id);
      if(cachedWs){
        this.workspaceService.setActiveWorkSpace(cachedWs);
      }
      this.organisationService.switchToOrg(id);
      return of(true);

    }else{
      return of(this.router.createUrlTree(['/login']));

    }

  }
}